// Stavební deník — okno pro uzavření záznamu (kalendářní dny v Europe/Prague).

const CONSTRUCTION_DIARY_EDIT_WINDOW_DAYS_DEFAULT = 7;

/** Načte env proměnnou bez reference na globál `process` (web build nemá @types/node). */
function readEnvString(key: string): string | undefined {
  const proc = (globalThis as { process?: { env?: Record<string, string | undefined> } }).process;
  const raw = proc?.env?.[key];
  return typeof raw === 'string' && raw.trim() !== '' ? raw.trim() : undefined;
}

/** Počet kalendářních dní (včetně dneška), po které lze neuzamčený deník uzavřít. */
export function getConstructionDiaryEditWindowDays(): number {
  const n = Number(readEnvString('CONSTRUCTION_DIARY_EDIT_WINDOW_DAYS'));
  if (Number.isFinite(n) && n > 0) return Math.floor(n);
  return CONSTRUCTION_DIARY_EDIT_WINDOW_DAYS_DEFAULT;
}

/** Uložené hodnoty počasí — stejné na webu, Androidu, iOS a v PDF. */
export const DIARY_WEATHER_CONDITIONS = [
  { value: 'SUNNY', label: 'Slunečno' },
  { value: 'CLOUDY', label: 'Oblačno' },
  { value: 'OVERCAST', label: 'Zataženo' },
  { value: 'RAIN', label: 'Déšť' },
  { value: 'SNOW', label: 'Sněžení' },
  { value: 'FROST', label: 'Mráz' },
  { value: 'WINDY', label: 'Větrno' },
] as const;

export type DiaryWeatherConditionCode = (typeof DIARY_WEATHER_CONDITIONS)[number]['value'];

export function diaryWeatherConditionLabel(
  code: string | null | undefined,
): string {
  if (!code) return '—';
  const found = DIARY_WEATHER_CONDITIONS.find((o) => o.value === code);
  return found?.label ?? '—';
}

/** Datum `YYYY-MM-DD` v časové zóně Europe/Prague. */
export function pragueCalendarDateIso(date: Date = new Date()): string {
  return new Intl.DateTimeFormat('en-CA', {
    timeZone: 'Europe/Prague',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).format(date);
}

/** Dnešní datum `YYYY-MM-DD` v časové zóně Europe/Prague. */
export function pragueCalendarTodayIso(): string {
  return pragueCalendarDateIso(new Date());
}

/**
 * Kalendářní den deníku `YYYY-MM-DD`.
 * Date-only string se bere doslova (sloupec `entry_date`);
 * timestamp / TypeORM `Date` → den v Europe/Prague (ne UTC, ne začátek týdne).
 */
export function normalizeDiaryEntryDateIso(value: string | Date | null | undefined): string {
  if (value instanceof Date) {
    if (!Number.isFinite(value.getTime())) return '';
    return pragueCalendarDateIso(value);
  }
  const raw = String(value ?? '').trim();
  if (/^\d{4}-\d{2}-\d{2}$/.test(raw)) return raw;
  if (!raw) return '';
  const date = new Date(raw);
  if (!Number.isFinite(date.getTime())) return '';
  return pragueCalendarDateIso(date);
}

/** Je ISO timestamp ve stejný kalendářní den `YYYY-MM-DD` (Europe/Prague)? */
export function isTimestampOnPragueDate(
  timestamp: string | Date | null | undefined,
  entryDate: string | Date | null | undefined,
): boolean {
  const day = normalizeDiaryEntryDateIso(entryDate);
  if (!timestamp || !day) return false;
  const date = timestamp instanceof Date ? timestamp : new Date(timestamp);
  if (!Number.isFinite(date.getTime())) return false;
  return pragueCalendarDateIso(date) === day;
}

/** Fail-closed: položky provedených prací jen pro den `entryDate` (Europe/Prague). */
export function filterWorkSummaryEntriesForDiaryDay<T extends { timestamp?: string | Date | null }>(
  items: readonly T[] | null | undefined,
  entryDate: string | Date | null | undefined,
): T[] {
  const day = normalizeDiaryEntryDateIso(entryDate);
  if (!day) return [];
  return (items ?? []).filter((item) => isTimestampOnPragueDate(item.timestamp, day));
}

/** Přičte kalendářní dny k ISO datu `YYYY-MM-DD`. */
function addCalendarDaysIso(isoDate: string, deltaDays: number): string {
  const [y, m, d] = isoDate.split('-').map((x) => parseInt(x, 10));
  const dt = new Date(Date.UTC(y, m - 1, d));
  dt.setUTCDate(dt.getUTCDate() + deltaDays);
  return dt.toISOString().slice(0, 10);
}

/** Nejstarší `entry_date`, pro které ještě lze deník uzavřít (včetně dneška). */
export function constructionDiaryEditWindowMinDate(
  todayIso: string,
  windowDays?: number,
): string {
  const days = windowDays ?? getConstructionDiaryEditWindowDays();
  return addCalendarDaysIso(todayIso, -(days - 1));
}

/** Lze uzavřít / upravit neuzamčený záznam s daným `entry_date`? */
export function isConstructionDiaryEntryDateEditable(
  entryDate: string | Date | null | undefined,
  todayIso?: string,
  windowDays?: number,
): boolean {
  const day = normalizeDiaryEntryDateIso(entryDate);
  if (!day) return false;
  const today = todayIso ?? pragueCalendarTodayIso();
  const min = constructionDiaryEditWindowMinDate(today, windowDays);
  return day >= min && day <= today;
}
